import { themeVar as colors } from "../design-tokens";
import { Band } from "../ui/Block";
import { CONDITION_LABELS, type Condition } from "./constants";
import { describeLines } from "./UpgradesPanel";
import { describeMarketConditions, type MarketConditions } from "./MarketConditionsPanel";
import { isUsableComp } from "./ReadinessGate";
import type { CandidateComp, SubjectGrid, ValueAdjustments } from "./types";

/**
 * STEP 4 — what the report is about to be built from, on one screen (2026-08-01).
 *
 * The old single review screen made this impossible: the thing you were confirming WAS the screen,
 * a scroll long enough that you could not hold the top of it in your head by the time you reached
 * the Generate button. Splitting the wizard means the last step can finally be a summary, and a
 * summary is the only place an agent can catch "wait, that's the wrong condition" before it is in a
 * PDF with their licence number on it.
 *
 * Two rules this screen keeps:
 *   - It reads state, never writes it. Every line points at the step that owns the edit rather than
 *     offering a second, competing control for the same field. Two editable copies of one value is
 *     how the two quietly disagree.
 *   - It shows no estimate. The figure does not exist until the engine computes it, and a preview
 *     assembled by the client would be a number with no source — the one thing this product cannot
 *     ship.
 */
export function ReviewSummary({
  subjectAnchor,
  grid,
  condition,
  comps,
  listings,
  valueAdjustments,
  extraContext,
  subjectHasPool,
  marketConditions,
  onEditSubject,
  onEditComps,
}: {
  subjectAnchor: string;
  grid: SubjectGrid;
  condition: string;
  comps: CandidateComp[];
  listings: CandidateComp[];
  valueAdjustments: ValueAdjustments;
  extraContext: string;
  subjectHasPool?: boolean;
  /** The agent's market-conditions (date of sale) override, when the flow collects one. OPTIONAL:
   * a caller that has not wired the control renders this screen exactly as before. Unlike the
   * lines above it this is not applied to the estimate afterwards -- it is a rate that produces a
   * real per-comparable row inside the reconciliation grid, so it is stated separately rather than
   * folded into the same list. */
  marketConditions?: MarketConditions;
  onEditSubject: () => void;
  onEditComps: () => void;
}) {
  const selected = comps.filter(isUsableComp);
  const adjustmentLines = describeLines(valueAdjustments, subjectHasPool);
  const marketConditionsLine = describeMarketConditions(marketConditions ?? {});

  /* A YEAR IS NOT A QUANTITY (2026-08-04). `toLocaleString()` was applied to every number, so the
     last screen before Generate read "Year built 2,001" — a thousands separator in a calendar
     year. Small, and it is on the one screen whose entire job is to let someone check the figures
     before signing them. Counts and areas keep their separators; identifiers do not. */
  const NO_GROUPING = new Set(["year", "beds", "garage", "full_baths", "half_baths",
                               "stories", "fireplaces", "neighborhood_code"]);

  const fact = (key: string): string | null => {
    const f = grid[key];
    if (!f?.checked || f.value === null || f.value === undefined || f.value === "") return null;
    if (typeof f.value === "boolean") return f.value ? "Yes" : "No";
    if (typeof f.value !== "number") return String(f.value);
    return NO_GROUPING.has(key) ? String(f.value) : f.value.toLocaleString();
  };

  const facts: [string, string][] = [];
  const gla = fact("gla");
  if (gla) facts.push(["Living area", `${gla} sq ft`]);
  const year = fact("year");
  if (year) facts.push(["Year built", year]);
  const lot = fact("lot_sf");
  if (lot) facts.push(["Lot", `${lot} sq ft`]);
  const beds = fact("beds");
  if (beds) facts.push(["Beds", beds]);
  const fullBaths = fact("full_baths");
  const halfBaths = fact("half_baths");
  if (fullBaths) facts.push(["Baths", halfBaths ? `${fullBaths} full / ${halfBaths} half` : `${fullBaths} full`]);
  const garage = fact("garage");
  if (garage) facts.push(["Garage", garage]);
  if (subjectHasPool) facts.push(["Pool", "Yes"]);

  return (
    <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 14 }}>
      <Block title="The home" onEdit={onEditSubject} editLabel="Edit the property detail">
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{subjectAnchor || "No address confirmed"}</p>
        {facts.length > 0 && (
          /* LABEL OVER VALUE, NOT LABEL-LEFT-VALUE-RIGHT (2026-08-30). `justify-content:
             space-between` in a 200px module pushed "Living area" and "2,418 sq ft" to opposite
             edges with ~90px of nothing between them, six times across the row — the same
             dead-middle the subject rows and the board cards both had, at a smaller scale. Stacked,
             each fact is one tight unit the eye reads in one movement, and the modules can be
             narrower, which is what stops "Garage" being orphaned onto a second row.

             160, not 180: the widest label here is "Living area" and the widest value
             "8,712 sq ft", so a stacked module needs about 110px of text. 160 fits seven across
             the review card at this measure instead of five-and-a-widow. */
          <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "2px 18px" }}>
            {facts.map(([label, value]) => (
              <div key={label} style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 1, borderBottom: `1px solid ${colors.border}`, padding: "6px 0" }}>
                <span style={{ color: colors.textSecondary, fontSize: 11.5 }}>{label}</span>
                <span style={{ fontFamily: "monospace", fontSize: 13.5 }}>{value}</span>
              </div>
            ))}
          </div>
        )}
        <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: colors.textSecondary }}>
          {/* `condition` is a plain string on the wire (the select writes whatever the server sent),
              so it is not narrowed to the label map's key union — an unrecognised value falls back
              to itself rather than rendering "undefined". */}
          Condition: <strong style={{ color: colors.textPrimary }}>{CONDITION_LABELS[condition as Condition] ?? condition}</strong>{" "}
          — your judgment, never read from a data source.
        </p>
      </Block>

      {/* THE TWO SHORT CARDS PAIR (2026-09-01, W3-UIFIX2). From
          `w2-uifix/after/step4-review@1920.png`: this screen renders at `content="form"`, so the
          page box is 1200 — and every card was full width for content that never exceeded ~640,
          stacked four deep. "The home" keeps the whole row because its fact strip is
          `repeat(auto-fill, minmax(160px, 1fr))` and genuinely uses it; these two are a short list
          and a short paragraph, and they are the same kind of statement seen from two sides — what
          the report is priced from, and what you added on top of it. `Band` pairs them at 588 each
          inside a 1200 page and stacks them below 904, so a laptop at 1280 and a phone are
          unchanged. */}
      <Band>
      <Block
        title={`Priced from ${selected.length} closed ${selected.length === 1 ? "sale" : "sales"}`}
        onEdit={onEditComps}
        editLabel="Edit the comparable sales"
      >
        {selected.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: colors.caution }}>
            Nothing is selected yet — the report can&apos;t be priced.
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {selected.map((c, i) => (
              <li key={`${c.name ?? "comp"}-${i}`} style={{ marginTop: i === 0 ? 0 : 4 }}>
                {c.name ?? "Unnamed sale"} —{" "}
                <span style={{ fontFamily: "monospace" }}>
                  ${Number(c.price).toLocaleString()}
                </span>
                {c._raw && Object.keys(c._raw).length > 0 && (
                  <span style={{ color: colors.caution, marginLeft: 6, fontSize: 12 }}>· you corrected this row</span>
                )}
              </li>
            ))}
          </ul>
        )}
        <p style={{ marginTop: 10, marginBottom: 0, fontSize: 13, color: colors.textSecondary }}>
          {listings.length > 0
            ? `${listings.length} home${listings.length === 1 ? "" : "s"} still on the market ${listings.length === 1 ? "appears" : "appear"} as the competition. ${listings.length === 1 ? "Its asking price" : "Their asking prices"} never enter the valuation.`
            : "No competing listings were found in what you uploaded."}
        </p>
      </Block>

      <Block title="What you added" onEdit={onEditSubject} editLabel="Edit the upgrades">
        {adjustmentLines.length === 0 && !marketConditionsLine && !extraContext.trim() ? (
          <p style={{ margin: 0, fontSize: 13, color: colors.textSecondary }}>
            Nothing — the report will be built from the comparable sales alone.
          </p>
        ) : (
          <>
            {adjustmentLines.length > 0 && (
              <>
                <p style={{ margin: 0, fontSize: 13, color: colors.textSecondary }}>
                  Applied to the estimate, attributed to you:
                </p>
                <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 13 }}>
                  {adjustmentLines.map((l) => (
                    <li key={l.label} style={{ marginTop: 3 }}>
                      {l.label} — <span style={{ fontFamily: "monospace" }}>{l.shown}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {marketConditionsLine && (
              <p style={{ marginTop: adjustmentLines.length > 0 ? 12 : 0, marginBottom: 0, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
                {marketConditionsLine}
              </p>
            )}
            {extraContext.trim() && (
              <p style={{ marginTop: adjustmentLines.length > 0 || marketConditionsLine ? 12 : 0, marginBottom: 0, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
                Plus {extraContext.trim().length} characters of context for the writing. It shapes
                the wording and moves no figure.
              </p>
            )}
          </>
        )}
      </Block>
      </Band>
    </div>
  );
}

function Block({
  title,
  onEdit,
  editLabel,
  children,
}: {
  title: string;
  onEdit: () => void;
  editLabel: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ background: colors.bgSurface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{title}</h3>
        <button className="vb-btn"
          type="button"
          onClick={onEdit}
          style={{ border: "none", background: "transparent", color: colors.accent, fontSize: 13, cursor: "pointer", padding: 0, textDecoration: "underline", whiteSpace: "nowrap" }}
        >
          {editLabel}
        </button>
      </div>
      {children}
    </section>
  );
}
